import React, { useState, useMemo, useEffect } from 'react';
import logo from './assets/logo_sps.png';
import {
  Printer,
  Search,
  AlertTriangle,
  Clock,
  MessageSquare,
  Calendar,
  Users,
  TrendingUp,
  Filter,
  WifiOff,
  Lock,
  Moon,
  Sun,
  LogOut,
  ChevronRight,
  Loader,
  Map as MapIcon
} from 'lucide-react';
import MapModal from './components/MapModal';

// --- COMPONENTS ---

const Card = ({ title, value, subtext, icon: Icon, colorClass }) => (
  <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6 shadow-sm flex items-start justify-between print:border-slate-300 print:shadow-none print:bg-white">
    <div>
      <p className="text-sm font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">{title}</p>
      <h3 className="text-3xl font-bold text-slate-900 dark:text-white mt-2 print:text-black">{value}</h3>
      {subtext && <p className="text-sm text-slate-400 dark:text-slate-500 mt-1">{subtext}</p>}
    </div>
    <div className={`p-3 rounded-lg ${colorClass} bg-opacity-10 dark:bg-opacity-20 print:hidden`}>
      <Icon className={`w-6 h-6 ${colorClass.replace('bg-', 'text-')}`} />
    </div>
  </div>
);

const SimpleBarChart = ({ data }) => {
  const maxVal = Math.max(...data.map(d => d.value), 1);

  return (
    <div className="mt-4">
      {/* Chart Area */}
      <div className="flex items-end justify-between h-32 gap-2 border-b border-slate-100 dark:border-slate-700 pb-2">
        {data.map((d, i) => (
          <div key={i} className="relative w-full h-full flex items-end justify-center group">

            {/* Tooltip */}
            <div className="absolute bottom-full mb-2 bg-slate-800 dark:bg-slate-100 text-white dark:text-slate-900 text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10 pointer-events-none shadow-lg font-bold">
              {d.value.toFixed(1)} hrs
            </div>

            {/* The Bar */}
            <div
              className="w-full bg-indigo-500 dark:bg-indigo-400 rounded-t-sm transition-all duration-500 hover:bg-indigo-600 dark:hover:bg-indigo-300 print:bg-slate-800"
              style={{
                height: d.value > 0 ? `${Math.max((d.value / maxVal) * 100, 2)}%` : '4px',
                opacity: d.value > 0 ? 1 : 0.2
              }}
            ></div>
          </div>
        ))}
      </div>

      {/* Labels Area */}
      <div className="flex justify-between gap-2 mt-2">
        {data.map((d, i) => (
          <div key={i} className="w-full text-center">
            <span className="text-xs text-slate-400 dark:text-slate-500 block truncate w-full">{d.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// --- LOGIN COMPONENT ---
const LoginPage = ({ onLogin }) => {
  const [user, setUser] = useState('');
  const [pass, setPass] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/loginreports', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user, password: pass })
      });

      if (response.ok) {
        const data = await response.json();
        onLogin(true, data.token);
      } else {
        const errData = await response.json();
        setError(errData.message || 'Invalid credentials. Access denied.');
        setPass('');
      }
    } catch (err) {
      console.error(err);
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <div className="bg-slate-800 border border-slate-700 w-full max-w-md rounded-2xl shadow-2xl overflow-hidden">
        <div className="p-8">
          <div className="flex justify-center mb-6">
            <div className="w-24 h-24 bg-white/5 rounded-full flex items-center justify-center p-4">
              <img src={logo} alt="SPS Logo" className="w-full h-full object-contain" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-center text-white mb-2">Secure Access</h2>
          <p className="text-center text-slate-400 mb-8">Enter your credentials to view the Executive Report.</p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-2">Email</label>
              <input
                type="text"
                value={user}
                onChange={(e) => setUser(e.target.value)}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all"
                placeholder="Enter email"
                disabled={loading}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-2">Password</label>
              <input
                type="password"
                value={pass}
                onChange={(e) => setPass(e.target.value)}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all"
                placeholder="Enter password"
                disabled={loading}
              />
            </div>

            {error && (
              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2 text-red-400 text-sm">
                <AlertTriangle className="w-4 h-4" />
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-3 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? <Loader className="w-4 h-4 animate-spin" /> : <>Access Dashboard <ChevronRight className="w-4 h-4" /></>}
            </button>
          </form>
        </div>
        <div className="bg-slate-900/50 p-4 text-center border-t border-slate-700">
          <p className="text-xs text-slate-500">Restricted Area • IPAPER Platform © 2026</p>
        </div>
      </div>
    </div>
  );
};

// --- MAIN APP COMPONENT ---

export default function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState(null);
  const [darkMode, setDarkMode] = useState(true); // Default Dark Mode
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [appliedStartDate, setAppliedStartDate] = useState('');
  const [appliedEndDate, setAppliedEndDate] = useState('');
  const [monthFilter, setMonthFilter] = useState('');
  const [tableData, setTableData] = useState([]);
  const [allMonths, setAllMonths] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [dataError, setDataError] = useState('');

  // Map Modal State
  const [isMapOpen, setIsMapOpen] = useState(false);
  const [mapTrackData, setMapTrackData] = useState([]);
  const [isMapLoading, setIsMapLoading] = useState(false);
  const [mapError, setMapError] = useState(null);
  const [alarmData, setAlarmData] = useState([]);

  const GOOGLE_MAPS_API_KEY = "AIzaSyAy3WEjEu1V7j-z7CdEhUp5U5_QTnIm1WU"; // Replace with env var if possible

  // Fetch report data from backend, optionally with date range
  const fetchReportData = async (startTimestamp, endTimestamp) => {
    setLoadingData(true);
    setDataError('');
    try {
      const body = startTimestamp && endTimestamp
        ? { startDate: startTimestamp, endDate: endTimestamp }
        : {};

      const response = await fetch('/api/getipaperreport', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        if (response.status === 401) {
          setIsAuthenticated(false);
          setToken(null);
          throw new Error('Session expired. Please login again.');
        }
        throw new Error('Failed to fetch report data.');
      }

      const rawData = await response.json();
      const mappedData = rawData.map(item => ({
        name: item.customer_name,
        phone: item.phone,
        email: item.email,
        month: item.activity_month,
        hours: parseFloat(item.hours_activity_span),
        gps: parseInt(item.total_gps_signals, 10),
        alarms: parseInt(item.total_alarms_fired, 10),
        alarmsSos: parseInt(item.alarms_type_2, 10),
        alarmsPre: parseInt(item.alarms_type_3, 10),
        messages: parseInt(item.total_messages_sent, 10)
      }));

      setTableData(mappedData);

      // On initial (unfiltered) load, store all available months for the dropdown
      if (!startTimestamp && !endTimestamp) {
        const months = [...new Set(mappedData.map(d => d.month))].sort().reverse();
        setAllMonths(months);
      }
    } catch (err) {
      console.error(err);
      setDataError(err.message);
    } finally {
      setLoadingData(false);
    }
  };

  // Handlers for mutual exclusivity
  const handleMonthChange = (e) => {
    const month = e.target.value;
    setMonthFilter(month);
    setStartDate('');
    setEndDate('');
    setAppliedStartDate('');
    setAppliedEndDate('');

    if (month) {
      const start = new Date(month + '-01T00:00:00');
      const end = new Date(start.getFullYear(), start.getMonth() + 1, 0, 23, 59, 59);
      fetchReportData(
        Math.floor(start.getTime() / 1000),
        Math.floor(end.getTime() / 1000)
      );
    } else {
      fetchReportData();
    }
  };

  const handleStartDateChange = (e) => {
    setStartDate(e.target.value);
  };

  const handleEndDateChange = (e) => {
    setEndDate(e.target.value);
  };

  const handleApplyDateRange = () => {
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
    setMonthFilter('');

    const start = new Date(startDate + 'T00:00:00');
    const end = new Date(endDate + 'T23:59:59');
    fetchReportData(
      Math.floor(start.getTime() / 1000),
      Math.floor(end.getTime() / 1000)
    );
  };

  const handleClearFilters = () => {
    setStartDate('');
    setEndDate('');
    setAppliedStartDate('');
    setAppliedEndDate('');
    setMonthFilter('');
    fetchReportData();
  };

  const handleViewMap = async (user) => {
    setIsMapOpen(true);
    setIsMapLoading(true);
    setMapError(null);
    setMapTrackData([]);
    setAlarmData([]);

    try {
      // Determine Start and End dates logic
      let startTimestamp, endTimestamp;

      if (monthFilter) {
        const start = new Date(monthFilter + '-01T00:00:00');
        const end = new Date(start.getFullYear(), start.getMonth() + 1, 0, 23, 59, 59);
        startTimestamp = Math.floor(start.getTime() / 1000);
        endTimestamp = Math.floor(end.getTime() / 1000);
      } else if (appliedStartDate && appliedEndDate) {
        const start = new Date(appliedStartDate + 'T00:00:00');
        const end = new Date(appliedEndDate + 'T23:59:59');
        startTimestamp = Math.floor(start.getTime() / 1000);
        endTimestamp = Math.floor(end.getTime() / 1000);
      } else {
        // Default to current month if no filter
        const now = new Date();
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
        startTimestamp = Math.floor(start.getTime() / 1000);
        endTimestamp = Math.floor(end.getTime() / 1000);
      }

      const response = await fetch('/api/getTrackForReports', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          email: user.email,
          startDate: startTimestamp,
          endDate: endTimestamp
        })
      });

      if (!response.ok) {
        throw new Error('Failed to fetch track data');
      }

      const data = await response.json();

      if (data.data && Array.isArray(data.data)) {
        setMapTrackData(data.data);
      } else {
        setMapError('No track data found for this period.');
      }

    } catch (err) {
      setMapError(err.message || "Failed to load map data.");
    } finally {
      setIsMapLoading(false);
    }
  };

  const handleViewAlarms = async (user) => {
    setIsMapOpen(true);
    setIsMapLoading(true);
    setMapError(null);
    setMapTrackData([]);
    setAlarmData([]);

    try {
      // Determine Start and End dates logic (Same as handleViewMap)
      let startTimestamp, endTimestamp;

      if (monthFilter) {
        const start = new Date(monthFilter + '-01T00:00:00');
        const end = new Date(start.getFullYear(), start.getMonth() + 1, 0, 23, 59, 59);
        startTimestamp = Math.floor(start.getTime() / 1000);
        endTimestamp = Math.floor(end.getTime() / 1000);
      } else if (appliedStartDate && appliedEndDate) {
        const start = new Date(appliedStartDate + 'T00:00:00');
        const end = new Date(appliedEndDate + 'T23:59:59');
        startTimestamp = Math.floor(start.getTime() / 1000);
        endTimestamp = Math.floor(end.getTime() / 1000);
      } else {
        const now = new Date();
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
        startTimestamp = Math.floor(start.getTime() / 1000);
        endTimestamp = Math.floor(end.getTime() / 1000);
      }

      const response = await fetch('/api/getAlarms', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          email: user.email,
          startDate: startTimestamp,
          endDate: endTimestamp
        })
      });

      if (!response.ok) {
        throw new Error('Failed to fetch alarms');
      }

      const data = await response.json();

      if (data.data && Array.isArray(data.data)) {
        setAlarmData(data.data);
        if (data.data.length === 0) {
          setMapError('No specific alarm locations found for this period.');
        }
      } else {
        setMapError('No alarm data found.');
      }

    } catch (err) {
      setMapError(err.message || "Failed to load alarm data.");
    } finally {
      setIsMapLoading(false);
    }
  };

  const handleLogin = (status, authToken) => {
    setIsAuthenticated(status);
    setToken(authToken);
  };

  // Toggle Dark Mode Class on Body/HTML
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  // Fetch Data on Authentication
  useEffect(() => {
    if (isAuthenticated && token) {
      fetchReportData();
    }
  }, [isAuthenticated, token]);

  // --- DATA PROCESSING ---
  const isWeeklyView = !!(appliedStartDate || monthFilter);

  const filteredData = useMemo(() => {
    // Client-side: only search filter. Date/month filtering is done server-side.
    let data = tableData.filter(row =>
      row.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      row.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Aggregate data by unique user (email)
    const aggregated = Object.values(data.reduce((acc, curr) => {
      if (!acc[curr.email]) {
        acc[curr.email] = {
          ...curr,
          months: [curr.month],
          hours: curr.hours,
          gps: curr.gps,
          alarms: curr.alarms,
          alarmsSos: curr.alarmsSos,
          alarmsPre: curr.alarmsPre,
          messages: curr.messages
        };
      } else {
        acc[curr.email].hours += curr.hours;
        acc[curr.email].gps += curr.gps;
        acc[curr.email].alarms += curr.alarms;
        acc[curr.email].alarmsSos += curr.alarmsSos;
        acc[curr.email].alarmsPre += curr.alarmsPre;
        acc[curr.email].messages += curr.messages;
        if (!acc[curr.email].months.includes(curr.month)) {
          acc[curr.email].months.push(curr.month);
        }
      }
      return acc;
    }, {}));

    const finalData = aggregated.map(item => ({
      ...item,
      displayMonth: item.months.length > 1
        ? `${item.months.length} ${isWeeklyView ? 'Weeks' : 'Months'}`
        : item.months[0]
    }));

    return finalData.sort((a, b) => b.hours - a.hours);
  }, [searchTerm, tableData]);

  const stats = useMemo(() => {
    const totalHours = filteredData.reduce((acc, curr) => acc + curr.hours, 0);
    const totalAlarms = filteredData.reduce((acc, curr) => acc + curr.alarms, 0);
    const totalMessages = filteredData.reduce((acc, curr) => acc + curr.messages, 0);
    const activeUsers = new Set(filteredData.filter(d => d.hours > 0).map(d => d.email)).size;
    const inactiveUsersCount = new Set(filteredData.filter(d => d.hours === 0).map(d => d.email)).size;

    const monthCounts = {};
    filteredData.forEach(d => {
      monthCounts[d.month] = (monthCounts[d.month] || 0) + d.hours;
    });
    const peakMonth = Object.keys(monthCounts).sort((a, b) => monthCounts[b] - monthCounts[a])[0] || 'N/A';

    return { totalHours, totalAlarms, totalMessages, activeUsers, inactiveUsersCount, peakMonth };
  }, [filteredData]);

  const chartActivity = useMemo(() => {
    if (tableData.length === 0) return [];

    if (isWeeklyView) {
      // Weekly data: labels like "2026-W09". Group by week and sum hours.
      const weekMap = {};
      tableData.forEach(row => {
        weekMap[row.month] = (weekMap[row.month] || 0) + row.hours;
      });
      return Object.keys(weekMap).sort().map(w => ({
        label: w.split('-')[1], // "W09" from "2026-W09"
        value: weekMap[w]
      }));
    }

    // Default: monthly (last 12 months)
    const months = [...new Set(tableData.map(d => d.month))].sort().reverse();
    const last12 = months.slice(0, 12).reverse();
    return last12.map(m => ({
      label: m.split('-')[1],
      value: tableData.filter(d => d.month === m).reduce((a, c) => a + c.hours, 0)
    }));
  }, [tableData, isWeeklyView]);

  const handlePrint = () => {
    window.print();
  };

  // --- RENDER ---

  if (!isAuthenticated) {
    return <LoginPage onLogin={handleLogin} />;
  }

  if (loadingData) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center text-white">
          <Loader className="w-10 h-10 animate-spin mx-auto mb-4 text-indigo-500" />
          <p>Loading report data...</p>
        </div>
      </div>
    );
  }

  if (dataError) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center text-red-400">
          <AlertTriangle className="w-10 h-10 mx-auto mb-4" />
          <p className="text-xl mb-4">{dataError}</p>
          <button
            onClick={() => setIsAuthenticated(false)}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-500"
          >
            Return to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen font-sans transition-colors duration-300 ${darkMode ? 'bg-slate-900 text-slate-100' : 'bg-slate-50 text-slate-800'} print:bg-white print:text-black`}>

      {/* --- NAVBAR --- */}
      <nav className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4 flex items-center justify-between print:hidden sticky top-0 z-20 shadow-sm transition-colors">
        <div className="flex items-center gap-3">
          <div className="h-8 flex items-center">
            <img src={logo} alt="SPS Logo" className="h-8 w-auto object-contain" />
          </div>
          <h1 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white">Executive Dashboard</h1>
        </div>

        <div className="flex items-center gap-4">
          {/* Search */}
          <div className="relative group">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4 group-focus-within:text-indigo-500 transition-colors" />
            <input
              type="text"
              placeholder="Search user..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 pr-4 py-2 bg-slate-100 dark:bg-slate-700 border-none rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none w-64 text-slate-900 dark:text-white transition-colors"
            />
          </div>

          {/* Month Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
            <select
              value={monthFilter}
              onChange={handleMonthChange}
              className="pl-9 pr-8 py-2 bg-slate-100 dark:bg-slate-700 border-none rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none appearance-none cursor-pointer text-slate-900 dark:text-white transition-colors"
            >
              <option value="">All Months</option>
              {allMonths.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>

          {/* Date Filter */}
          <div className="flex items-center gap-2">
            <div className="relative">
              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">From</span>
              <input
                type="date"
                value={startDate}
                onChange={handleStartDateChange}
                className="pl-10 pr-2 py-2 bg-slate-100 dark:bg-slate-700 border-none rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none text-slate-900 dark:text-white transition-colors"
              />
            </div>
            <div className="relative">
              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">To</span>
              <input
                type="date"
                value={endDate}
                onChange={handleEndDateChange}
                className="pl-8 pr-2 py-2 bg-slate-100 dark:bg-slate-700 border-none rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none text-slate-900 dark:text-white transition-colors"
              />
            </div>
            <button
              onClick={handleApplyDateRange}
              disabled={!startDate || !endDate}
              className="px-3 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-500 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Apply
            </button>
            {(appliedStartDate || appliedEndDate || monthFilter) && (
              <button
                onClick={handleClearFilters}
                className="px-3 py-2 bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-slate-200 text-sm font-medium rounded-lg hover:bg-slate-300 dark:hover:bg-slate-500 transition-colors"
              >
                Clear
              </button>
            )}
          </div>

          {/* Theme Toggle */}
          <button
            onClick={() => setDarkMode(!darkMode)}
            className="p-2 rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
            title="Toggle Theme"
          >
            {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>

          {/* Print */}
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 bg-slate-900 dark:bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-slate-800 dark:hover:bg-indigo-500 transition-colors shadow-lg shadow-indigo-500/20"
          >
            <Printer className="w-4 h-4" />
            Print Report
          </button>

          {/* Logout */}
          <button
            onClick={() => setIsAuthenticated(false)}
            className="p-2 text-slate-400 hover:text-red-500 transition-colors"
            title="Logout"
          >
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </nav>

      {/* --- MAIN CONTENT --- */}
      <main className="max-w-7xl mx-auto px-6 py-8 print:px-0 print:py-0 print:max-w-none">

        {/* PRINT HEADER */}
        <div className="hidden print:flex items-center justify-between mb-8 pb-4 border-b border-slate-900">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Executive Report</h1>
            <p className="text-slate-500 mt-1">Customer Usage & Risk Analysis</p>
          </div>
          <div className="text-right">
            <p className="text-sm font-bold text-slate-900">IPAPER PLATFORM</p>
            <p className="text-xs text-slate-500">{new Date().toLocaleDateString()}</p>
          </div>
        </div>

        {/* --- KPI SECTION --- */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8 print:grid-cols-4 print:gap-4">
          <Card
            title="Total Active Hours"
            value={stats.totalHours.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            subtext="Cumulative usage time"
            icon={Clock}
            colorClass="bg-blue-500"
          />
          <Card
            title="Risk Alerts"
            value={stats.totalAlarms}
            subtext="SOS & Live Tracking events"
            icon={AlertTriangle}
            colorClass="bg-red-500"
          />
          <Card
            title="Active Users"
            value={stats.activeUsers}
            subtext={`${stats.inactiveUsersCount} Inactive (0 hours)`}
            icon={Users}
            colorClass="bg-emerald-500"
          />
          <Card
            title="Peak Activity"
            value={stats.peakMonth}
            subtext="Highest traffic month"
            icon={Calendar}
            colorClass="bg-purple-500"
          />
        </div>

        {/* --- CHARTS SECTION --- */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8 print:grid-cols-2 print:mb-4">
          <div className="lg:col-span-2 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6 shadow-sm print:shadow-none print:border-slate-300 print:bg-white">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white print:text-black mb-4">{isWeeklyView ? 'Activity Trend (Weekly)' : 'Activity Trend (Last 12 Months)'}</h3>
            <SimpleBarChart data={chartActivity} />
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6 shadow-sm print:shadow-none print:border-slate-300 print:bg-white">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white print:text-black mb-4">Highest Risk Users</h3>
            <div className="space-y-4">
              {filteredData
                .filter(u => u.alarms > 0)
                .sort((a, b) => b.alarms - a.alarms)
                .slice(0, 5)
                .map((user, idx) => (
                  <div key={idx} className="flex items-center justify-between group">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 flex items-center justify-center text-xs font-bold border border-red-100 dark:border-red-500/20">
                        {idx + 1}
                      </div>
                      <div className="truncate max-w-[120px]">
                        <p className="text-sm font-medium text-slate-900 dark:text-slate-200 truncate">{user.name}</p>
                        <p className="text-xs text-slate-400 dark:text-slate-500 truncate">{user.displayMonth}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 dark:bg-red-500/20 text-red-800 dark:text-red-300 border border-transparent dark:border-red-500/20">
                        {user.alarms} Alarms
                      </span>
                    </div>
                  </div>
                ))}
              {filteredData.filter(u => u.alarms > 0).length === 0 && <p className="text-sm text-slate-400">No high-risk users found.</p>}
            </div>
          </div>
        </div>

        {/* --- PAGE BREAK FOR PRINT --- */}
        <div className="print:break-before-page"></div>

        {/* --- DETAILED TABLE --- */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden print:shadow-none print:border-t print:border-slate-300 print:rounded-none print:bg-white">
          <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/50 flex items-center justify-between print:bg-white print:px-0">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white print:text-black">Customer Activity Detail</h3>
            <span className="text-xs font-medium text-slate-500 dark:text-slate-400 bg-slate-200 dark:bg-slate-700 px-2 py-1 rounded print:hidden">
              {filteredData.length} Records
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 dark:bg-slate-900/50 text-slate-500 dark:text-slate-400 print:bg-slate-100 print:text-slate-900">
                <tr>
                  <th className="px-6 py-3 font-semibold print:px-2">Customer / Email</th>
                  <th className="px-6 py-3 font-semibold print:px-2">{appliedStartDate ? 'Interval' : 'Month'}</th>
                  <th className="px-6 py-3 font-semibold text-right print:px-2">Active Hours</th>
                  <th className="px-6 py-3 font-semibold text-right print:px-2">GPS Signals</th>
                  <th className="px-6 py-3 font-semibold text-right print:px-2">SOS</th>
                  <th className="px-6 py-3 font-semibold text-right print:px-2">Live Tracking</th>
                  <th className="px-6 py-3 font-semibold text-right print:px-2">Total Alarms</th>
                  <th className="px-6 py-3 font-semibold text-right print:px-2">Messages</th>
                  <th className="px-6 py-3 font-semibold text-center print:hidden">Ver Mapa</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700 border-t border-slate-100 dark:border-slate-700">
                {filteredData.map((row, index) => (
                  <tr
                    key={index}
                    className={`hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors 
                      ${row.alarms > 0 ? 'bg-red-50/30 dark:bg-red-900/10 print:bg-transparent' : ''}
                      ${row.hours === 0 ? 'text-slate-400 dark:text-slate-600 bg-slate-50/30 dark:bg-slate-800/50' : ''}
                    `}
                  >
                    <td className="px-6 py-3 print:px-2">
                      <div className={`font-medium ${row.hours === 0 ? 'text-slate-500 dark:text-slate-500' : 'text-slate-900 dark:text-slate-200'} print:text-black`}>{row.name}</div>
                      <div className="text-xs text-slate-500 dark:text-slate-500 print:text-slate-600 truncate max-w-[200px]">{row.email}</div>
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap print:px-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium print:bg-transparent print:p-0 
                        ${row.hours === 0 ? 'bg-slate-100 dark:bg-slate-700 text-slate-500' : 'bg-indigo-50 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300 print:text-slate-800'}`}>
                        {row.displayMonth}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-right font-mono print:px-2">
                      {row.hours > 0 ? (
                        <span className="text-slate-600 dark:text-slate-300">{row.hours.toFixed(2)}</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-slate-400 dark:text-slate-600 text-xs">
                          <WifiOff className="w-3 h-3" /> 0
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-3 text-right font-mono text-slate-600 dark:text-slate-400 print:px-2">
                      {row.gps > 0 ? row.gps.toLocaleString() : '-'}
                    </td>
                    {/* SOS */}
                    <td className="px-6 py-3 text-right print:px-2 font-mono text-red-500 dark:text-red-400">
                      {row.alarmsSos > 0 ? row.alarmsSos : '-'}
                    </td>
                    {/* Pre-panic */}
                    <td className="px-6 py-3 text-right print:px-2 font-mono text-orange-500 dark:text-orange-400">
                      {row.alarmsPre > 0 ? row.alarmsPre : '-'}
                    </td>
                    {/* Total Alarms */}
                    <td className="px-6 py-3 text-right print:px-2">
                      {row.alarms > 0 ? (
                        <button
                          onClick={() => handleViewAlarms(row)}
                          className="inline-flex items-center gap-1 text-red-600 dark:text-red-400 font-bold hover:underline focus:outline-none"
                          title="View Alarms on Map"
                        >
                          <AlertTriangle className="w-3 h-3" /> {row.alarms}
                        </button>
                      ) : (
                        <span className="text-slate-300 dark:text-slate-600">-</span>
                      )}
                    </td>
                    <td className="px-6 py-3 text-right print:px-2">
                      {row.messages > 0 ? (
                        <span className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400 font-medium">
                          <MessageSquare className="w-3 h-3" /> {row.messages}
                        </span>
                      ) : (
                        <span className="text-slate-300 dark:text-slate-600">-</span>
                      )}
                    </td>
                    <td className="px-6 py-3 text-center print:hidden">
                      <button
                        onClick={() => handleViewMap(row)}
                        className="inline-flex items-center justify-center p-2 text-indigo-600 hover:text-indigo-900 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors dark:bg-indigo-900/20 dark:text-indigo-400 dark:hover:bg-indigo-900/40"
                        title="View Map"
                      >
                        <MapIcon className="w-5 h-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredData.length === 0 && (
            <div className="p-12 text-center text-slate-400 dark:text-slate-500">
              <Search className="w-12 h-12 mx-auto mb-3 opacity-20" />
              <p>No records found matching your filters.</p>
            </div>
          )}
        </div>

        {/* PRINT FOOTER */}
        <div className="hidden print:block mt-8 text-center text-xs text-slate-400 border-t border-slate-200 pt-4">
          Generated automatically by IPAPER Dashboard System • Page 2
        </div>

      </main>
      <MapModal
        isOpen={isMapOpen}
        onClose={() => setIsMapOpen(false)}
        trackData={mapTrackData}
        alarmData={alarmData}
        loading={isMapLoading}
        error={mapError}
        mapApiKey={GOOGLE_MAPS_API_KEY}
      />
    </div>
  );
}